import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Users,
  // Building2,
  MapPin,
  Briefcase,
  Star,
  ChevronRight,
  Search,
  Newspaper,
  SlidersHorizontal,
  CheckCircle2,
} from "lucide-react";

import logo from "../src/assets/logo_xingnex.png";

// screenshots
import expressionPage from "../src/assets/expressionPage.png";
import XingPage from "../src/assets/XingPage.png";
import xingProfile from "../src/assets/xingProfile.png";
import SearchBusiness from "../src/assets/SearchBusiness.png";
import businessCategoryPage from "../src/assets/businessCategoryPage.png";
import businessPage from "../src/assets/businessPage.png";
import businessPage2 from "../src/assets/businessPage2.png";
import businessPage3 from "../src/assets/businessPage3.png";
import xingBusinessSearch from "../src/assets/xingBusinessSearch.png";
import filtersPage from "../src/assets/filtersPage.png";
import newsPage from "../src/assets/newsPage.png";
import newsPage2 from "../src/assets/newsPage2.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const features = [
  {
    icon: <MapPin className="h-6 w-6" />,
    title: "Nearby discovery that feels relevant",
    text: "Find people, businesses, and activity near you based on location, category, and intent.",
  },
  {
    icon: <ShieldCheck className="h-6 w-6" />,
    title: "Trust-centered business visibility",
    text: "Profiles are built to help users feel confidence quickly and help businesses stand out with credibility.",
  },
  {
    icon: <Briefcase className="h-6 w-6" />,
    title: "Business tools built into discovery",
    text: "From directory visibility to quote requests and direct contact, Xingnex helps discovery turn into action.",
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: "Social + utility in one experience",
    text: "Connect with people, engage with expressions, and discover local businesses in the same platform.",
  },
];

const audience = [
  {
    title: "For users",
    points: [
      "Find trusted local people and services faster",
      "Discover nearby businesses with real context",
      "Explore posts, profiles, and local activity in one place",
    ],
  },
  {
    title: "For businesses",
    points: [
      "Show up where customer discovery starts",
      "Present a stronger trust-first profile",
      "Drive calls, quotes, messages, and growth",
    ],
  },
];

const plans = [
  {
    name: "Entry",
    price: "$0",
    description: "Basic visibility for businesses entering the network.",
    items: ["Visibility", "Profile presence", "Access to discovery"],
  },
  {
    name: "Basic",
    price: "$12/mo",
    description: "Trust-first presence for businesses that want credibility.",
    items: ["Trusted profile validation", "Visibility", "Foundational presence"],
  },
  {
    name: "Pro",
    price: "$30/mo",
    description: "Growth tools for businesses ready to convert attention into action.",
    items: ["Trusted profile validation", "Visibility", "CRM tools", "Analytics"],
    featured: true,
  },
  {
    name: "Premium",
    price: "$30/mo + boost",
    description: "Enhanced reach and preferred placement for serious growth.",
    items: [
      "Trusted validation",
      "Visibility",
      "CRM tools",
      "Analytics",
      "Boosted visibility",
      "Featured placement",
    ],
  },
];

const steps = [
  "Create your profile",
  "Discover nearby people and businesses",
  "Engage through posts, profiles, and direct actions",
  "Convert discovery into relationships and growth",
];

const screenshots = [
  {
    image: expressionPage,
    title: "Local business feed",
    text: "A clean discovery feed for business expressions, promotions, and nearby visibility.",
  },
  {
    image: XingPage,
    title: "Nearby Xing users",
    text: "Map-based social discovery helps users find relevant people in their area.",
  },
  {
    image: xingProfile,
    title: "Rich user profiles",
    text: "Profile screens support identity, social presence, and expression-based engagement.",
  },
  {
    image: SearchBusiness,
    title: "Business directory search",
    text: "Users can search by category and explore services in a familiar, mobile-first experience.",
  },
  {
    image: businessCategoryPage,
    title: "Category exploration",
    text: "Browse categories to quickly move from interest to relevant local businesses.",
  },
  {
    image: businessPage,
    title: "Business profile overview",
    text: "A polished business page with trust, location, contact, and service details.",
  },
  {
    image: businessPage2,
    title: "Business details and online presence",
    text: "Structured sections make offerings, website, and business details easy to scan.",
  },
  {
    image: businessPage3,
    title: "Business listing cards",
    text: "Category pages highlight featured businesses and direct actions like call or quote.",
  },
  {
    image: xingBusinessSearch,
    title: "Map + directory search",
    text: "Business discovery combines map awareness with familiar list-based results.",
  },
  {
    image: filtersPage,
    title: "Personalized discovery controls",
    text: "Users can tune radius, nearby content, and the discovery experience to fit intent.",
  },
  {
    image: newsPage,
    title: "News experience",
    text: "A curated news layer keeps users informed while staying inside the platform.",
  },
  {
    image: newsPage2,
    title: "Category-based news feed",
    text: "Users can browse different news categories with a structured, readable layout.",
  },
];

function SectionHeading({ eyebrow, title, text }) {
  return (
    <div className="max-w-3xl mx-auto text-center mb-14">
      <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-700 mb-4">
        {eyebrow}
      </p>
      <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-zinc-900">
        {title}
      </h2>
      <p className="mt-5 text-base md:text-lg text-zinc-600 leading-8">
        {text}
      </p>
    </div>
  );
}

function ScreenshotCard({ image, title, text }) {
  return (
    <motion.div
      variants={fadeUp}
      className="rounded-[1.75rem] border border-zinc-200 bg-white overflow-hidden shadow-[0_18px_50px_rgba(2,12,27,0.08)]"
    >
      <div className="p-3">
        <div className="rounded-[1.25rem] overflow-hidden border border-zinc-200 bg-zinc-50">
          <img
            src={image}
            alt={title}
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </div>
      <div className="px-5 pb-6 pt-1 text-left">
        <h3 className="text-lg font-semibold text-zinc-900">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-zinc-600">{text}</p>
      </div>
    </motion.div>
  );
}

export default function XingnexLandingPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "user",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!formData.email.trim()) {
      alert("Please enter your email address.");
      return;
    }
  
    setIsSubmitting(true);
  
    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
        }),
      });
  
      const contentType = response.headers.get("content-type") || "";
      let data;
  
      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(text || "Server returned a non-JSON response.");
      }
  
      if (!response.ok) {
        throw new Error(data.message || data.error || "Something went wrong.");
      }
  
      alert("You’re on the waitlist 🚀");
  
      setFormData({
        name: "",
        email: "",
        role: "user",
      });
    } catch (error) {
      console.error("Waitlist submit error:", error);
      alert(error.message || "Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(64,249,155,0.12),_transparent_32%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-24 md:pt-10 md:pb-32">
          <motion.header
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <img
                src={logo}
                alt="Xingnex Logo"
                className="h-12 w-12 object-contain"
              />
              <div className="text-left">
                <p className="font-semibold text-lg tracking-tight text-zinc-900">
                  Xingnex
                </p>
                <p className="text-xs uppercase tracking-[0.22em] text-zinc-500">
                  Connection • Discovery • Network
                </p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <a
                href="#product"
                className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition"
              >
                Product
              </a>
              <a
                href="#pricing"
                className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition"
              >
                Pricing
              </a>
              <a
                href="#waitlist"
                className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:opacity-90 transition"
              >
                Join Waitlist
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.header>

          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center mt-16 md:mt-24">
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="max-w-2xl text-left"
            >
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 mb-6"
              >
                <Star className="h-4 w-4" />
                A modern platform for local discovery, trust, and growth
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="text-5xl md:text-7xl font-semibold tracking-[-0.05em] leading-[0.95] text-zinc-950"
              >
                Discover people.
                <br />
                Find businesses.
                <br />
                Build trusted connections.
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-7 text-lg md:text-xl leading-8 text-zinc-600 max-w-xl"
              >
                Xingnex is a location-driven social discovery and business
                growth platform that helps people find relevant nearby
                connections and helps businesses earn trust where discovery
                begins.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-9 flex flex-col sm:flex-row gap-4"
              >
                <a
                  href="#waitlist"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 px-6 py-4 text-white font-semibold shadow-xl hover:opacity-95 transition"
                >
                  Join the Waitlist
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#product"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-zinc-300 bg-white px-6 py-4 text-zinc-900 font-medium hover:bg-zinc-50 transition"
                >
                  Explore the Product
                  <ChevronRight className="h-4 w-4" />
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="relative"
            >
              <div className="absolute -inset-8 bg-emerald-100/60 blur-3xl rounded-full" />

              <div className="relative grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                <div className="pt-8">
                  <div className="rounded-[2rem] border border-zinc-200 bg-white p-3 shadow-2xl">
                    <img
                      src={xingProfile}
                      alt="Xingnex profile screen"
                      className="rounded-[1.5rem] w-full h-auto block"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-[2rem] border border-zinc-200 bg-white p-3 shadow-2xl">
                    <img
                      src={expressionPage}
                      alt="Xingnex expression feed"
                      className="rounded-[1.5rem] w-full h-auto block"
                    />
                  </div>
                  <div className="rounded-[2rem] border border-zinc-200 bg-white p-3 shadow-2xl">
                    <img
                      src={XingPage}
                      alt="Xingnex map screen"
                      className="rounded-[1.5rem] w-full h-auto block"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Xingnex"
            title="Built for modern local discovery"
            text="Xingnex connects people, businesses, posts, and local visibility in one mobile-first experience."
          />

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid md:grid-cols-2 xl:grid-cols-4 gap-6"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeUp}
                className="rounded-3xl border border-zinc-200 bg-white p-7 shadow-sm hover:shadow-lg transition"
              >
                <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-zinc-900">
                  {feature.title}
                </h3>
                <p className="mt-3 text-zinc-600 leading-7 text-sm md:text-base">
                  {feature.text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="product" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Product showcase"
            title="See how Xingnex works across the full experience"
            text="Explore people discovery, business search, categories, filters, profiles, expressions, and news."
          />

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.08 }}
            className="grid md:grid-cols-2 xl:grid-cols-3 gap-7"
          >
            {screenshots.map((shot) => (
              <ScreenshotCard
                key={shot.title}
                image={shot.image}
                title={shot.title}
                text={shot.text}
              />
            ))}
          </motion.div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="How it works"
            title="Simple for users. Powerful for businesses."
            text="The platform is designed to feel intuitive on the surface while giving businesses better ways to be found, trusted, and contacted."
          />

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="rounded-[2rem] border border-zinc-200 bg-zinc-900 text-white p-8 md:p-10 shadow-2xl">
              <p className="text-sm uppercase tracking-[0.25em] text-emerald-300">
                User journey
              </p>

              <div className="mt-8 space-y-6">
                {steps.map((step, idx) => (
                  <div key={step} className="flex gap-4 items-start">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-sm font-semibold">
                      0{idx + 1}
                    </div>

                    <div>
                      <p className="text-lg font-medium">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-6">
              {audience.map((group) => (
                <div
                  key={group.title}
                  className="rounded-[2rem] border border-zinc-200 bg-white p-8 shadow-sm"
                >
                  <h3 className="text-2xl font-semibold tracking-tight text-zinc-900">
                    {group.title}
                  </h3>

                  <div className="mt-5 space-y-4">
                    {group.points.map((point) => (
                      <div key={point} className="flex items-start gap-3">
                        <div className="mt-1 h-6 w-6 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                          <ChevronRight className="h-4 w-4" />
                        </div>
                        <p className="text-zinc-600 leading-7">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="rounded-[2rem] border border-zinc-200 bg-white p-8">
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    {
                      icon: <Search className="h-5 w-5" />,
                      label: "Search",
                    },
                    {
                      icon: <SlidersHorizontal className="h-5 w-5" />,
                      label: "Filter",
                    },
                    {
                      icon: <Newspaper className="h-5 w-5" />,
                      label: "Stay informed",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 text-left"
                    >
                      <div className="h-11 w-11 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
                        {item.icon}
                      </div>
                      <p className="font-semibold text-zinc-900">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Pricing"
            title="Flexible tiers for businesses at every stage"
            text="Start with visibility, then scale into trust, analytics, CRM workflows, and boosted placement."
          />

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-[2rem] p-7 border shadow-sm flex flex-col ${
                  plan.featured
                    ? "bg-zinc-900 text-white border-zinc-900 shadow-2xl"
                    : "bg-white text-zinc-900 border-zinc-200"
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-2xl font-semibold">{plan.name}</h3>
                  {plan.featured && (
                    <span className="rounded-full bg-emerald-400/20 text-emerald-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
                      Popular
                    </span>
                  )}
                </div>

                <div className="mt-5">
                  <p className="text-3xl font-semibold tracking-tight">
                    {plan.price}
                  </p>
                  <p
                    className={`mt-3 text-sm leading-6 ${
                      plan.featured ? "text-zinc-300" : "text-zinc-600"
                    }`}
                  >
                    {plan.description}
                  </p>
                </div>

                <div className="mt-6 space-y-3 flex-1">
                  {plan.items.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div
                        className={`mt-1 h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${
                          plan.featured
                            ? "bg-white/10 text-emerald-300"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </div>
                      <p
                        className={`text-sm leading-6 ${
                          plan.featured ? "text-zinc-200" : "text-zinc-600"
                        }`}
                      >
                        {item}
                      </p>
                    </div>
                  ))}
                </div>

                <a
                  href="#waitlist"
                  className={`mt-8 inline-flex items-center justify-center rounded-2xl px-5 py-3 font-medium transition ${
                    plan.featured
                      ? "bg-white text-zinc-900 hover:bg-zinc-100"
                      : "bg-zinc-900 text-white hover:opacity-90"
                  }`}
                >
                  Get Early Access
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-[2.5rem] bg-white text-zinc-900 px-8 py-12 md:px-14 md:py-16 shadow-2xl border border-zinc-200">
            <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
              <div className="text-left">
                <p className="text-sm uppercase tracking-[0.24em] text-emerald-700">
                  Built for the next phase of local connection
                </p>

                <h2 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
                  Xingnex is building a stronger bridge between community discovery and business growth.
                </h2>

                <p className="mt-6 text-zinc-600 text-base md:text-lg leading-8 max-w-2xl">
                  Whether someone is looking for nearby people, relevant services,
                  trusted local businesses, or timely content, Xingnex is designed
                  to make discovery feel useful and action-ready.
                </p>
              </div>

              <div
                id="waitlist"
                className="rounded-[2rem] bg-zinc-50 text-zinc-900 p-6 md:p-7 shadow-sm border border-zinc-200"
              >
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  Join the waitlist
                </p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                  Get early access
                </h3>
                <p className="mt-3 text-zinc-600 leading-7 text-sm md:text-base">
                  Be first to hear when Xingnex launches for users and businesses.
                </p>

                <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full rounded-2xl border border-zinc-300 px-4 py-3.5 outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                  />

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email address"
                    className="w-full rounded-2xl border border-zinc-300 px-4 py-3.5 outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                    required
                  />

                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-zinc-300 px-4 py-3.5 outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                  >
                    <option value="user">I’m interested as a user</option>
                    <option value="business">I’m interested as a business</option>
                    <option value="both">I’m interested as both</option>
                  </select>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3.5 font-medium text-white hover:opacity-90 transition disabled:opacity-60"
                  >
                    {isSubmitting ? "Submitting..." : "Request Early Access"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}